import { DateTime } from "luxon";
import React, { useState } from "react";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  makeStyles,
  Menu,
  MenuItem
} from "@material-ui/core";
import DropDownIcon from "@material-ui/icons/ArrowDropDown";

const useStyles = makeStyles(theme => ({
  currentDateRange: {
    cursor: "pointer",
    borderBottom: "1px solid rgba(0, 0, 0, 0.3)"
  },
  dateRangeWrapper: {
    display: "flex",
    lineHeight: "60px",
    padding: "40px 0"
  },
  dateRangeDash: {
    padding: "0 20px"
  },
  dateInput: {
    paddingLeft: theme.spacing(2),
    width: "200px",
    fontFamily: theme.typography.fontFamily
  }
}));

function formatDateForDisplay(isoDate: string): string {
  return DateTime.fromISO(isoDate).toLocaleString(DateTime.DATE_MED);
}

export type DateRangePickerShortCut = {
  startDate: string;
  endDate: string;
  title: string;
};

export default function DateRangePicker({
  timezone,
  startDate,
  endDate,
  shortcuts,
  onChange
}: {
  timezone: string;
  startDate: string;
  endDate: string;
  shortcuts: DateRangePickerShortCut[];
  onChange: (startDate: string, endDate: string) => void;
}): JSX.Element {
  const classes = useStyles();

  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");

  const now = DateTime.now().setZone(timezone);

  function handleDropdownSelect(newStartDate: string, newEndDate: string) {
    setMenuAnchorEl(null);
    setDialogOpen(false);
    onChange(newStartDate, newEndDate);
  }

  function handleDropdownSelectCustom() {
    setMenuAnchorEl(null);
    setDialogOpen(true);
    setCustomStartDate("");
    setCustomEndDate("");
  }

  function handleDropdownClick(evt: React.MouseEvent<HTMLElement>) {
    evt.preventDefault();
    setMenuAnchorEl(evt.currentTarget);
  }

  return (
    <div>
      <span className={classes.currentDateRange} onClick={handleDropdownClick}>
        {formatDateForDisplay(startDate)} - {formatDateForDisplay(endDate)}
        <DropDownIcon
          style={{ position: "relative", top: 6, fill: "rgba(0, 0, 0, 0.3)" }}
        />
      </span>
      <Menu
        anchorEl={menuAnchorEl}
        keepMounted
        open={!!menuAnchorEl}
        onClose={() => setMenuAnchorEl(null)}
      >
        {shortcuts.map(shortcut => (
          <MenuItem
            onClick={() =>
              handleDropdownSelect(shortcut.startDate, shortcut.endDate)
            }
          >
            {shortcut.title}
          </MenuItem>
        ))}
        <MenuItem onClick={handleDropdownSelectCustom}>
          Select A Date Range...
        </MenuItem>
      </Menu>
      <Dialog
        title="Select A Date Range"
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
      >
        <DialogTitle>Select a Date Range</DialogTitle>
        <DialogContent>
          <div className={classes.dateRangeWrapper}>
            <input
              type="date"
              className={classes.dateInput}
              value={customStartDate}
              onChange={evt => setCustomStartDate(evt.target.value)}
              max={customEndDate || now.toISODate()}
            />
            <div className={classes.dateRangeDash}>-</div>
            <input
              type="date"
              className={classes.dateInput}
              value={customEndDate}
              onChange={evt => setCustomEndDate(evt.target.value)}
              min={customStartDate || undefined}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            color="primary"
            variant="contained"
            disabled={!customStartDate || !customEndDate}
            onClick={() => {
              handleDropdownSelect(customStartDate!, customEndDate!);
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
